import { faPlus, faSliders } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useAtom } from "@dbeining/react-atom";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { formatEwcDisplayCode } from "../../components/Forms/EwcCodeInput";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import type { ISiteOption } from "../../customTypings/customer/Customer";
import type { IRole } from "../../customTypings/user/Role";
import type { IWasteMovementListRow, WasteMovementDirection } from "../../customTypings/wasteMovement/WasteMovement";
import { sitesService } from "../../services/sites/sites.service";
import { wasteMovementsService } from "../../services/wasteMovements/wasteMovements.service";
import {
    addEntityActionLabel,
    buildRecordLifecycleConfirmProps,
    dateRangeFilters,
    EntityLabels,
    getUserFacingErrorMessage, resolveActionErrorMessage,
    includeDeletedSitesAdminFilter,
    logErrorForDev,
    recordLifecycleListConfirmBodies,
    relatedEntityTextColumn,
    resolveSingleSiteIdPin,
    Role,
    SectionLabels,
    showToastSuccess,
    SITE_RELATED_ENTITY_LIFECYCLE,
} from "../../utils";
import {
    buildWasteMovementAdminPagedListActions,
    canMutateWasteMovements,
    formatWasteMovementToastLabel,
    getWasteMovementAdminActionSuccessMessage,
    type WasteMovementAdminConfirmKind,
} from "./wmAdminListActions";
import { filterReportingEligibleSites } from "@/features/wasteMovement";

type MovementRow = IWasteMovementListRow;
type ConfirmKind = WasteMovementAdminConfirmKind | null;

function formatGeographyCode(code?: string | null): string {
    const c = (code ?? "").trim();
    return c || "-";
}

const yesNoCodeFilterOptions: Record<string, string | null> = {
    All: null,
    Yes: "Yes",
    No: "No",
};

function centerTextColumn(member: string, title: string, width: number): IColumn {
    return { member, renderAs: ColumnRender.text, title, width, align: "center" } as IColumn;
}

function formatDate(value: string | undefined): string {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

export type WasteMovementListPageConfig = {
    movementDirection: WasteMovementDirection;
    listTitle: string;
    entityLabel: string;
    listPath: string;
    editPathPrefix: string;
    description: string;
    persistanceKey: string;
    siteStorageKey: string;
};

export function WasteMovementListPage({ config }: { config: WasteMovementListPageConfig }) {
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const canMutate = canMutateWasteMovements(roleNames);
    const hasReportingEligibleSite = useMemo(
        () => filterReportingEligibleSites(sites).length > 0,
        [sites]
    );
    const canCreate = canMutate && hasReportingEligibleSite;
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );
    const [sitesLoading, setSitesLoading] = useState(true);
    const [sitesError, setSitesError] = useState<string | null>(null);
    const showSiteColumn = useMemo(() => sites.length > 1, [sites.length]);
    const singleSiteId = sites.length === 1 ? sites[0].id : null;

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmId, setConfirmId] = useState<string | null>(null);
    const [confirmSiteId, setConfirmSiteId] = useState<string | null>(null);
    const [confirmLabel, setConfirmLabel] = useState("");

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                setSitesError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev(`${config.persistanceKey}.sites`, e);
                setSitesError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setSitesLoading(false));
    }, [config.persistanceKey]);

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmId(null);
        setConfirmSiteId(null);
        setConfirmLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmId || !confirmKind || !confirmSiteId) return;
        const op =
            confirmKind === "disable"
                ? wasteMovementsService.disable(confirmSiteId, confirmId)
                : confirmKind === "enable"
                  ? wasteMovementsService.enable(confirmSiteId, confirmId)
                  : confirmKind === "softDelete"
                    ? wasteMovementsService.softDelete(confirmSiteId, confirmId)
                    : wasteMovementsService.restore(confirmSiteId, confirmId);
        op.then(() => {
            showToastSuccess(getWasteMovementAdminActionSuccessMessage(confirmKind, confirmLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: MovementRow) => {
        setConfirmKind(kind);
        setConfirmId(item.id);
        setConfirmSiteId(item.siteId);
        setConfirmLabel(formatWasteMovementToastLabel(item.reference, item.ewcCodeSnapshot));
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                config.entityLabel,
                confirmLabel,
                recordLifecycleListConfirmBodies(confirmLabel)
            ),
        [confirmKind, config.entityLabel, confirmLabel]
    );

    const siteFilterOptions = useMemo(() => {
        const opts: Record<string, string | null> = { "All sites": null };
        for (const s of sites) opts[s.siteName] = s.id;
        return opts;
    }, [sites]);

    const columns: IColumn[] = useMemo(() => {
        const ewcColumn: IColumn = {
            member: "ewcCodeSnapshot",
            renderAs: ColumnRender.customRenderer,
            title: "EWC Code",
            width: 100,
            align: "center",
            customRenderer: (item: MovementRow) => (
                <td className="text-center font-monospace">
                    {formatEwcDisplayCode(item.ewcCodeSnapshot) || "-"}
                </td>
            ),
        } as IColumn;

        const base: IColumn[] = [
            {
                member: "movementDate",
                renderAs: ColumnRender.customRenderer,
                title: "Date",
                width: 110,
                customRenderer: (item: MovementRow) => <td>{formatDate(item.movementDate)}</td>,
            } as IColumn,
        ];
        if (showSiteColumn) {
            base.push(
                relatedEntityTextColumn({
                    member: "siteName",
                    title: "Site",
                    width: 140,
                    relatedEntityLifecycle: SITE_RELATED_ENTITY_LIFECYCLE,
                })
            );
        }
        base.push({ member: "reference", renderAs: ColumnRender.text, title: "Reference", width: 120 } as IColumn);

        if (config.movementDirection === "Received") {
            base.push(
                {
                    member: "originGeographyCodeSnapshot",
                    renderAs: ColumnRender.customRenderer,
                    title: "Origin",
                    width: 220,
                    customRenderer: (item: MovementRow) => (
                        <td className="small">{formatGeographyCode(item.originGeographyCodeSnapshot)}</td>
                    ),
                } as IColumn,
                ewcColumn,
                centerTextColumn("disposalRecoveryCodeSnapshot", "Disposal/Recovery", 120),
                centerTextColumn("municipalFlagCodeSnapshot", "Municipal Source", 120),
                centerTextColumn("degradableFlagCodeSnapshot", "Degradable", 90),
                centerTextColumn("physicalStateCodeSnapshot", "State", 90),
                centerTextColumn("sourceActivityCodeSnapshot", "Source Activity", 140),
                { member: "preTreatmentCodeSnapshot", renderAs: ColumnRender.text, title: "Pre-Treatment", width: 140 } as IColumn,
                centerTextColumn("weightTonnes", "Tonnes", 90),
                centerTextColumn("reportingStatus", "Reporting Status", 120)
            );
        } else {
            base.push(
                {
                    member: "destinationGeographyCodeSnapshot",
                    renderAs: ColumnRender.customRenderer,
                    title: "Destination",
                    width: 220,
                    customRenderer: (item: MovementRow) => (
                        <td className="small">{formatGeographyCode(item.destinationGeographyCodeSnapshot)}</td>
                    ),
                } as IColumn,
                ewcColumn,
                centerTextColumn("municipalFlagCodeSnapshot", "Municipal Source", 120),
                centerTextColumn("physicalStateCodeSnapshot", "State", 90),
                centerTextColumn("disposalRecoveryCodeSnapshot", "Disposal/Recovery", 120),
                centerTextColumn("weightTonnes", "Tonnes", 90),
                centerTextColumn("reportingStatus", "Reporting Status", 120)
            );
        }

        return base;
    }, [config.movementDirection, showSiteColumn]);

    const filters: IFilter[] = useMemo(() => {
        const list: IFilter[] = [];
        if (sites.length > 1) {
            list.push({
                title: "Site",
                member: "siteId",
                type: FilterType.select,
                options: siteFilterOptions,
                defaultValue: null,
            } as IFilter);
        }

        list.push(
            ...dateRangeFilters("Movement date", "movementDateFrom", "movementDateTo"),
            { title: "Reference", member: "referenceSearch", type: FilterType.text } as IFilter,
            ...(config.movementDirection === "Received"
                ? [{ title: "Origin", member: "originSearch", type: FilterType.text } as IFilter]
                : [{ title: "Destination", member: "destinationSearch", type: FilterType.text } as IFilter]),
            { title: "EWC Code", member: "ewcCodeSearch", type: FilterType.text } as IFilter,
            ...(config.movementDirection === "Received"
                ? [{ title: "Disposal/Recovery", member: "disposalRecoverySearch", type: FilterType.text } as IFilter]
                : []),
            {
                title: "Municipal Source",
                member: "municipalFlagCode",
                type: FilterType.select,
                options: yesNoCodeFilterOptions,
                defaultValue: null,
            } as IFilter,
            ...(config.movementDirection === "Received"
                ? [
                      {
                          title: "Degradable",
                          member: "degradableFlagCode",
                          type: FilterType.select,
                          options: yesNoCodeFilterOptions,
                          defaultValue: null,
                      } as IFilter,
                  ]
                : []),
            { title: "State", member: "physicalStateSearch", type: FilterType.text } as IFilter,
            ...(config.movementDirection === "Received"
                ? [
                      { title: "Source Activity", member: "sourceActivitySearch", type: FilterType.text } as IFilter,
                      { title: "Pre-Treatment", member: "preTreatmentSearch", type: FilterType.text } as IFilter,
                  ]
                : [{ title: "Disposal/Recovery", member: "disposalRecoverySearch", type: FilterType.text } as IFilter]),
            { title: "Tonnes From", member: "weightTonnesFrom", type: FilterType.integer, defaultValue: null } as IFilter,
            { title: "Tonnes To", member: "weightTonnesTo", type: FilterType.integer, defaultValue: null } as IFilter,
            {
                title: "Reporting Status",
                member: "reportingStatus",
                type: FilterType.select,
                options: {
                    All: null,
                    Draft: "Draft",
                    Ready: "Ready",
                    Included: "Included",
                    Excluded: "Excluded",
                    Corrected: "Corrected",
                },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
            ...(isAdmin ? [includeDeletedSitesAdminFilter()] : [])
        );
        return list;
    }, [sites.length, siteFilterOptions, config.movementDirection, isAdmin]);

    const resolveStaticFilterValues = useCallback(
        (filterData: Record<string, unknown>) => ({
            movementDirection: config.movementDirection,
            ...resolveSingleSiteIdPin(filterData, sites.length, singleSiteId),
        }),
        [config.movementDirection, sites.length, singleSiteId]
    );

    const itemActions: IPagedListAction[] = useMemo(() => {
        const viewAction: IPagedListAction = {
            text: "View",
            onClick: (item: MovementRow) =>
                navigate(`${config.editPathPrefix}/${item.id}`, { state: { siteId: item.siteId } }),
            primary: !canMutate,
        } as IPagedListAction;

        if (!canMutate) return [viewAction];

        return [
            viewAction,
            {
                text: "Edit",
                onClick: (item: MovementRow) =>
                    navigate(`${config.editPathPrefix}/${item.id}`, { state: { siteId: item.siteId } }),
                primary: true,
                hideWhenMembers: ["siteNotOperationallyEligible"],
            } as IPagedListAction,
            ...buildWasteMovementAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [canMutate, navigate, config.editPathPrefix]);

    const navigateToDefaults = useCallback(() => {
        navigate(`${config.listPath}/defaults`);
    }, [navigate, config.listPath]);

    const headerNavActions: IPagedListAction[] = useMemo(() => {
        if (!canCreate) return [];
        return [
            {
                text: "Your Defaults",
                icon: faSliders,
                onClick: () => navigateToDefaults(),
            } as IPagedListAction,
        ];
    }, [canCreate, navigateToDefaults]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!canCreate) return [];
        return [
            {
                text: addEntityActionLabel(config.entityLabel),
                icon: faPlus,
                onClick: () => navigate(`${config.editPathPrefix}/new`),
                primary: true,
            } as IPagedListAction,
        ];
    }, [canCreate, navigate, config.entityLabel, config.editPathPrefix]);

    return (
        <Container fluid>
            <Helmet>
                <title>{config.listTitle}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        className="mb-3"
                        items={[
                            { text: SectionLabels.operations, to: "/operations" },
                            { text: config.listTitle },
                        ]}
                    />
                    <div
                        className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                        style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
                    >
                        <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                            {config.listTitle}
                        </h1>
                        {canCreate && (
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                {!isNarrowScreen && (
                                    <Button variant="secondary" onClick={navigateToDefaults}>
                                        Your Defaults
                                    </Button>
                                )}
                                {isNarrowScreen && (
                                    <EllipsisActionMenu
                                        id={`${config.persistanceKey}-header-actions`}
                                        ariaLabel={`${config.listTitle} actions`}
                                        actions={headerNavActions}
                                        item={{}}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <p className="text-muted">{config.description}</p>
                    {sitesError && <Alert variant="danger">{sitesError}</Alert>}
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    {sitesLoading ? (
                        <Alert variant="info">Loading sites…</Alert>
                    ) : sites.length === 0 ? (
                        <Alert variant="warning">No sites are available for your account.</Alert>
                    ) : (
                        <PagedList
                            key={`${config.persistanceKey}-${showSiteColumn ? "multi" : "single"}`}
                            columns={columns}
                            filters={filters}
                            resolveStaticFilterValues={resolveStaticFilterValues}
                            headActions={headActions}
                            itemActions={itemActions}
                            rowActionsAriaLabel={`${config.entityLabel} actions`}
                            showDeletedRowIndicator
                            defaultSortBy="MovementDate"
                            defaultSortOrder={SortOrder.descending}
                            getUrl="/api/waste-movements/all"
                            rowFormatter={() => null}
                            persistanceKey={config.persistanceKey}
                            refreshNumber={refreshNumber}
                        />
                    )}
                </Col>
            </Row>
            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
}
